import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { InstallPrompt } from "./InstallPrompt";
import { TourButton } from "../tour/TourButton";
import { useCanSeeUsage } from "../usage/useCanSeeUsage";
import logoUrl from "../assets/uss-tech-logo.png";

/** The header's plain-link chrome — Usage and Offline sit side by side. */
const HEADER_LINK = {
  padding: "5px 13px",
  background: "#252525",
  color: "#bbb",
  border: "1px solid #3a3a3a",
  borderRadius: 4,
  textDecoration: "none",
  fontSize: 12,
} as const;

export function AppHeader(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const canSeeUsage = useCanSeeUsage();

  const teamSlug = auth.team?.slug ?? null;
  const label =
    auth.displayName !== null && auth.displayName.length > 0
      ? `${auth.displayName}${teamSlug !== null ? ` (${teamSlug})` : ""}`
      : (auth.team?.displayName ?? teamSlug ?? "Anonymous");

  const onSignOut = async (): Promise<void> => {
    try {
      await auth.logout();
    } catch {
      // best-effort — AuthContext.logout already swallows transport errors,
      // but if it ever rejects we still want the user routed to /login.
    }
    navigate("/login", { replace: true });
  };

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: "2px 16px",
        borderBottom: "1px solid #2a2a2a",
        background: "#111",
        color: "#eee",
        height: 72,
        boxSizing: "border-box",
      }}
    >
      <Link to="/flights" style={{ display: "flex", alignItems: "center" }}>
        <img
          src={logoUrl}
          alt="USS Technologies"
          // The logo PNG has black "USS Technologies" text on transparent
          // bg + a teal mark — illegible on dark. invert(1) flips black→
          // white but also flips teal to pink; hue-rotate(180deg) swings
          // it back to teal. Net effect: white text, original teal mark.
          style={{
            height: 68,
            width: "auto",
            display: "block",
            filter: "invert(1) hue-rotate(180deg)",
          }}
        />
      </Link>
      <span
        style={{
          fontFamily: "system-ui",
          fontSize: "1.25rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
        }}
      >
        Hawk Atlas
      </span>
      <span style={{ flex: 1 }} />
      <InstallPrompt />
      {canSeeUsage && (
        <Link to="/usage" data-tour="header.usage" style={HEADER_LINK}>
          Usage
        </Link>
      )}
      <Link to="/offline" data-tour="header.offline" style={HEADER_LINK}>
        Offline
      </Link>
      <TourButton />
      <span data-tour="header.identity" style={{ fontFamily: "system-ui", color: "#bbb" }}>
        Signed in as <strong style={{ color: "#fff" }}>{label}</strong>
      </span>
      {auth.team?.role === "admin" && (
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: 4,
            backgroundColor: "#c00",
            color: "#fff",
            letterSpacing: 0.5,
          }}
        >
          ADMIN
        </span>
      )}
      <button
        type="button"
        onClick={() => void onSignOut()}
        style={{
          padding: "5px 13px",
          background: "#252525",
          color: "#bbb",
          border: "1px solid #3a3a3a",
          borderRadius: 4,
          cursor: "pointer",
          fontSize: 12,
        }}
      >
        Sign out
      </button>
    </header>
  );
}
